import { Investment } from '../../../types';

export interface PaymentData {
  downPaymentAmount: number;
  totalFinanced: number;
  totalInterest: number;
  monthlyFinancials: MonthlyFinancialData[];
  annualSummaries: AnnualFinancialSummary[];
  buybackPrices: Array<{ year: number; month: number; price: number; date: string }>;
  amortizationTable: AmortizationEntry[];
}

export interface MonthlyFinancialData {
  month: number;
  year: number;
  date: string;
  operationalReturn: number;
  capitalAppreciation: number;
  totalReturn: number;
  certificateBuybackPrice: number;
  cumulativeOperationalReturn: number;
  cumulativeCapitalAppreciation: number;
  cumulativeTotalReturn: number;
}

export interface AnnualFinancialSummary {
  year: number;
  totalOperationalReturn: number;
  totalCapitalAppreciation: number;
  totalReturn: number;
  certificateValueAtYearEnd: number;
  monthlyData: MonthlyFinancialData[];
}

export interface AmortizationEntry {
  paymentNumber: number;
  date: string;
  paymentAmount: number;
  principalAmount: number;
  interestAmount: number;
  remainingBalance: number;
  cumulativePrincipal: number;
  cumulativeInterest: number;
}

export function calculatePaymentData(investment: Investment): PaymentData {
  const totalInvestment = investment.initialCertificates * investment.certificateBasePrice;
  const downPaymentAmount = totalInvestment * (investment.downPaymentPercentage || 30) / 100;
  const totalFinanced = totalInvestment - downPaymentAmount;
  const interestRate = (investment.financingInterestRate || 0) / 100 / 12; // Monthly interest rate
  const numberOfPayments = 48;
  
  // Calculate monthly payment using standard amortization formula
  let monthlyPayment: number;
  if (interestRate > 0) {
    monthlyPayment = totalFinanced * (interestRate * Math.pow(1 + interestRate, numberOfPayments)) / 
                    (Math.pow(1 + interestRate, numberOfPayments) - 1);
  } else {
    monthlyPayment = totalFinanced / numberOfPayments; // No interest
  }
  
  const totalInterest = (monthlyPayment * numberOfPayments) - totalFinanced;
  
  // Generate amortization table
  const amortizationTable: AmortizationEntry[] = [];
  let remainingBalance = totalFinanced;
  let cumulativePrincipal = 0;
  let cumulativeInterest = 0;
  
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() + 1); // Start next month
  
  for (let i = 1; i <= numberOfPayments; i++) {
    const interestAmount = remainingBalance * interestRate;
    const principalAmount = monthlyPayment - interestAmount;
    remainingBalance -= principalAmount;
    cumulativePrincipal += principalAmount;
    cumulativeInterest += interestAmount;
    
    // Calculate payment date
    const paymentDate = new Date(startDate);
    paymentDate.setMonth(startDate.getMonth() + i - 1);
    
    amortizationTable.push({
      paymentNumber: i,
      date: paymentDate.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      paymentAmount: monthlyPayment,
      principalAmount,
      interestAmount,
      remainingBalance: Math.max(0, remainingBalance),
      cumulativePrincipal,
      cumulativeInterest
    });
  }
  
  // Generate buyback prices for 30 years
  const buybackPrices: Array<{ year: number; month: number; price: number; date: string }> = [];
  const initialPrice = investment.certificateBasePrice;
  const appreciationRate = investment.appreciationRate / 100; // 12% annual
  const lemonPriceIncrease = investment.lemonPriceIncrease / 100; // 5% annual after year 5
  
  for (let year = 1; year <= 30; year++) {
    for (let month = 1; month <= 12; month++) {
      let price: number;
      
      if (year <= 5) {
        // First 5 years: 12% annual appreciation
        const monthsElapsed = (year - 1) * 12 + month;
        const monthlyAppreciationRate = Math.pow(1 + appreciationRate, 1/12) - 1;
        price = initialPrice * Math.pow(1 + monthlyAppreciationRate, monthsElapsed);
      } else {
        // After year 5: max appreciated value + lemon price increase
        const maxAppreciatedValue = initialPrice * Math.pow(1 + appreciationRate, 5);
        if (investment.increaseLemonPrice) {
          const monthsAfterYear5 = (year - 5 - 1) * 12 + month;
          const monthlyLemonIncrease = Math.pow(1 + lemonPriceIncrease, 1/12) - 1;
          price = maxAppreciatedValue * Math.pow(1 + monthlyLemonIncrease, monthsAfterYear5);
        } else {
          price = maxAppreciatedValue;
        }
      }
      
      // Calculate date
      const date = new Date(2025, month - 1, 15); // 15th of each month
      date.setFullYear(2025 + year - 1);
      
      buybackPrices.push({
        year,
        month,
        price,
        date: date.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      });
    }
  }
  
  // Generate monthly financial data for returns simulator
  const monthlyFinancials: MonthlyFinancialData[] = [];
  const averageProductionPerHectare = investment.averageProductionPerHectare;
  const averageSalePricePerKg = investment.averageSalePricePerKg;
  
  let cumulativeOperationalReturn = 0;
  let cumulativeCapitalAppreciation = 0;
  let cumulativeTotalReturn = 0;
  let previousCertificatePrice = initialPrice;
  
  for (let year = 1; year <= 30; year++) {
    for (let month = 1; month <= 12; month++) {
      const monthIndex = (year - 1) * 12 + month - 1;
      const buybackPrice = buybackPrices[monthIndex];
      
      // Calculate operational return (only after year 4)
      let monthlyOperationalReturn = 0;
      if (year > 4) {
        // Calculate current lemon price with increase
        let currentLemonPrice = averageSalePricePerKg;
        if (year > 5 && investment.increaseLemonPrice) {
          currentLemonPrice = averageSalePricePerKg * Math.pow(1 + lemonPriceIncrease, year - 5);
        }
        
        // Monthly utility per certificate: (production × price × 0.1 × 0.65) / 12
        monthlyOperationalReturn = (averageProductionPerHectare * currentLemonPrice * 0.1 * 0.65) / 12;
      }
      
      // Calculate capital appreciation for this month
      const monthlyCapitalAppreciation = buybackPrice.price - previousCertificatePrice;
      
      // Calculate total return for this month
      const monthlyTotalReturn = monthlyOperationalReturn + monthlyCapitalAppreciation;
      
      // Update cumulative values
      cumulativeOperationalReturn += monthlyOperationalReturn;
      cumulativeCapitalAppreciation += monthlyCapitalAppreciation;
      cumulativeTotalReturn += monthlyTotalReturn;
      
      monthlyFinancials.push({
        month: (year - 1) * 12 + month,
        year,
        date: buybackPrice.date,
        operationalReturn: monthlyOperationalReturn,
        capitalAppreciation: monthlyCapitalAppreciation,
        totalReturn: monthlyTotalReturn,
        certificateBuybackPrice: buybackPrice.price,
        cumulativeOperationalReturn,
        cumulativeCapitalAppreciation,
        cumulativeTotalReturn
      });
      
      previousCertificatePrice = buybackPrice.price;
    }
  }
  
  // Generate annual summaries
  const annualSummaries: AnnualFinancialSummary[] = [];
  for (let year = 1; year <= 30; year++) {
    const yearlyData = monthlyFinancials.filter(data => data.year === year);
    const totalOperationalReturn = yearlyData.reduce((sum, data) => sum + data.operationalReturn, 0);
    const totalCapitalAppreciation = yearlyData.reduce((sum, data) => sum + data.capitalAppreciation, 0);
    const totalReturn = totalOperationalReturn + totalCapitalAppreciation;
    const certificateValueAtYearEnd = yearlyData[yearlyData.length - 1]?.certificateBuybackPrice || 0;
    
    annualSummaries.push({
      year,
      totalOperationalReturn,
      totalCapitalAppreciation,
      totalReturn,
      certificateValueAtYearEnd,
      monthlyData: yearlyData
    });
  }
  
  return {
    downPaymentAmount,
    totalFinanced,
    totalInterest,
    monthlyFinancials,
    annualSummaries,
    buybackPrices,
    amortizationTable
  };
}